/* =========================================================================
   use-in-view.ts
   IntersectionObserver hook for scroll-triggered reveals.
   ========================================================================= */

import { useState, useRef, useEffect, useCallback } from "react";

interface UseInViewOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  onEnter?: () => void;
}

export function useInView(options: UseInViewOptions = {}) {
  const {
    threshold = 0.1,
    rootMargin = "0px",
    triggerOnce = true,
    onEnter,
  } = options;
  const [isInView, setIsInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTriggered = useRef(false);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!hasTriggered.current) {
                setIsInView(true);
                hasTriggered.current = true;
                onEnter?.();
              }
              if (triggerOnce) {
                observer.disconnect();
              }
            } else if (!triggerOnce) {
              setIsInView(false);
            }
          });
        },
        { threshold, rootMargin },
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [threshold, rootMargin, triggerOnce, onEnter],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { ref, isInView };
}
